import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { FJDetailsListItem, FJDetailsListItemFailed } from '../../types/FJDetails';
import { GENERATE_STATE } from '../../constants/FJEnumConstant';

export type {
    FJDetailsListItem,
    FJDetailsListItemFailed,
    FJDetailsListItemSuccess,
    FJDetailsSubtitleItem,
    FJDetailsVideoData,
} from '../../types/FJDetails';

export type FJDetailsPageState = {
    items: FJDetailsListItem[];
    selectedId: string | null;
    /** 侧栏项目主标题（来自最近文件或生成预览） */
    projectTitle: string;
    /** 详情列表多选：已勾选的 clip id */
    selectedClipIds: string[];
};

const initialState: FJDetailsPageState = {
    items: [],
    selectedId: null,
    projectTitle: '',
    selectedClipIds: [],
};

const fjDetailsPageSlice = createSlice({
    name: 'detailsPage',
    initialState,
    reducers: {
        hideDetailsPage: state => {
            state.items = [];
            state.selectedId = null;
            state.projectTitle = '';
            state.selectedClipIds = [];
        },
        setDetailsProjectTitle: (state, action: PayloadAction<string>) => {
            state.projectTitle = action.payload;
        },
        selectDetailsItem: (state, action: PayloadAction<string>) => {
            state.selectedId = action.payload;
        },
        toggleDetailsItemLiked: (state, action: PayloadAction<string>) => {
            const row = state.items.find(i => i.id === action.payload);
            if (row && 'liked' in row) {
                row.liked = !row.liked;
            }
        },
        setDetailsItemStatus: (
            state,
            action: PayloadAction<{ id: string; status: typeof GENERATE_STATE.PROCESSING }>,
        ) => {
            const row = state.items.find(i => i.id === action.payload.id);
            if (row) {
                row.status = action.payload.status;
            }
        },
        updateDetailsItemSubEventId: (state, action: PayloadAction<{ id: string; subEventId: string }>) => {
            const index = state.items.findIndex(i => i.id === action.payload.id);
            if (index !== -1) {
                state.items[index].subEventId = action.payload.subEventId;
            }
        },
        toggleDetailsItemDisliked: (state, action: PayloadAction<string>) => {
            const row = state.items.find(i => i.id === action.payload);
            if (row && 'disliked' in row) {
                row.disliked = !row.disliked;
            }
        },
        retryDetailsItem: (state, action: PayloadAction<string>) => {
            const i = state.items.findIndex(x => x.id === action.payload);
            if (i === -1) {
                return;
            }
            const cur = state.items[i];
            if (cur.status !== GENERATE_STATE.FAILED) {
                return;
            }
            const failed = cur as FJDetailsListItemFailed;
            state.items[i] = {
                id: failed.id,
                title: failed.title,
                status: GENERATE_STATE.PROCESSING,
                thumbnailUrl: failed.thumbnailUrl,
                subEventId: failed.subEventId,
            };
        },
        renameDetailsItem: (state, action: PayloadAction<{ id: string; title: string }>) => {
            const row = state.items.find(i => i.id === action.payload.id);
            if (row) {
                row.title = action.payload.title;
            }
        },
        removeDetailsItem: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            state.items = state.items.filter(i => i.id !== id);
            state.selectedClipIds = state.selectedClipIds.filter(sid => sid !== id);
            if (state.selectedId === id) {
                state.selectedId = state.items[0]?.id ?? null;
            }
        },
        setDetailsSelectedClipIds: (state, action: PayloadAction<string[]>) => {
            state.selectedClipIds = action.payload.map(String);
        },
        toggleDetailsClipSelected: (state, action: PayloadAction<string>) => {
            const id = String(action.payload);
            const list = state.selectedClipIds.map(String);
            const index = list.indexOf(id);
            if (index >= 0) {
                list.splice(index, 1);
            } else {
                list.push(id);
            }
            state.selectedClipIds = list;
        },
        clearDetailsClipSelection: state => {
            state.selectedClipIds = [];
        },
        setDetailsItems: (state, action: PayloadAction<FJDetailsListItem[]>) => {
            state.items = action.payload;
            const validIds = new Set(action.payload.map(i => i.id));
            state.selectedClipIds = state.selectedClipIds.filter(id => validIds.has(id));
            if (state.selectedId && !action.payload.some(i => i.id === state.selectedId)) {
                state.selectedId = action.payload[0]?.id ?? null;
            }
        },
        updateDetailsItem: (state, action: PayloadAction<FJDetailsListItem>) => {
            const index = state.items.findIndex(i => i.id === action.payload.id);
            if (index !== -1) {
                state.items[index] = action.payload;
            }
        },
    },
});

export const {
    hideDetailsPage,
    selectDetailsItem,
    toggleDetailsItemLiked,
    toggleDetailsItemDisliked,
    retryDetailsItem,
    renameDetailsItem,
    removeDetailsItem,
    setDetailsItems,
    setDetailsProjectTitle,
    setDetailsItemStatus,
    updateDetailsItem,
    updateDetailsItemSubEventId,
    setDetailsSelectedClipIds,
    toggleDetailsClipSelected,
    clearDetailsClipSelection,
} = fjDetailsPageSlice.actions;

export default fjDetailsPageSlice.reducer;
